import { cva, type VariantProps } from "class-variance-authority";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { RING_ROLES } from "../../lib/colors";

/**
 * The roles Spinner offers, and the source of both its `cva` keys and `SpinnerProps["color"]` — so
 * the two cannot drift apart. Spinner carries `ai`, which the surface components have no use for.
 */
export type SpinnerColor = keyof typeof RING_ROLES;

const spinnerVariants = cva("border-4 rounded-full animate-spin border-t-transparent", {
  variants: {
    size: {
      xxs: "w-2 h-2 border-2",
      xs: "w-4 h-4 border-[3px]",
      sm: "w-8 h-8",
      md: "w-16 h-16",
      lg: "w-24 h-24",
    },
    color: {
      primary: RING_ROLES.primary.ring,
      secondary: RING_ROLES.secondary.ring,
      destructive: RING_ROLES.destructive.ring,
      muted: RING_ROLES.muted.ring,
      white: RING_ROLES.white.ring,
      ai: RING_ROLES.ai.ring,
    } satisfies Record<SpinnerColor, string>,
  },
  defaultVariants: {
    size: "md",
    color: "primary",
  },
});

const labelVariants = cva("text-sm text-center", {
  variants: {
    color: {
      primary: RING_ROLES.primary.label,
      secondary: RING_ROLES.secondary.label,
      destructive: RING_ROLES.destructive.label,
      muted: RING_ROLES.muted.label,
      white: RING_ROLES.white.label,
      ai: RING_ROLES.ai.label,
    } satisfies Record<SpinnerColor, string>,
  },
  defaultVariants: {
    color: "primary",
  },
});

export interface SpinnerProps extends VariantProps<typeof spinnerVariants> {
  /** Additional CSS classes applied to the spinning element. */
  className?: string;
  /**
   * The size of the spinner.
   * @default "md"
   */
  size?: "xxs" | "xs" | "sm" | "md" | "lg";
  /**
   * The color theme of the spinner. `white` is a literal, theme-fixed white for spinners on a
   * dark/colored/photo surface — it does not invert in dark mode.
   * @default "primary"
   */
  color?: SpinnerColor;
  /** Optional label rendered next to the spinner; its color tracks the `color` variant (faded). */
  children?: ReactNode;
  /**
   * Layout of the optional label relative to the spinner. Only applies when `children` are present.
   * @default "column"
   */
  direction?: "row" | "column";
}

/**
 * A loading indicator with an animated spinning ring, optionally labeled.
 *
 * @example
 * ```tsx
 * <Spinner />
 * <Spinner size="sm" color="secondary" />
 * <Spinner direction="row">Loading your data</Spinner>
 * ```
 */
export const Spinner = ({ size, color, className, children, direction = "column" }: SpinnerProps) => {
  const { t } = useTranslation();
  const loadingLabel = t("Loading...");

  const spinner = (
    <div className="relative" data-slot="spinner">
      <div
        className={cn(
          spinnerVariants({ size }),
          // The track is a faint full ring behind the spinning arc so the spinner reads as a ring,
          // not just a lone moving sliver. Its tone is per-role in the registry: `white` needs a
          // translucent white track for the dark surfaces it sits on, every other role the muted one.
          RING_ROLES[color ?? "primary"].track
        )}
      />
      <div
        className={cn(spinnerVariants({ size, color }), "absolute top-0 left-0 border-t-transparent", className)}
        role="status"
        aria-label={loadingLabel}
      >
        <span className="sr-only">{loadingLabel}</span>
      </div>
    </div>
  );

  if (children === undefined || children === null) return spinner;

  return (
    <div className={cn("flex items-center justify-center gap-2", direction === "column" ? "flex-col" : "flex-row")}>
      {spinner}
      <span className={cn(labelVariants({ color }))}>{children}</span>
    </div>
  );
};
